<template>
  <table class="w-100 table info-table table-sm totals-table">

    <tr v-if="!hideHammer">
      <td class="border-top-0"><b>{{ $t('Hammer') }}</b></td>
      <td class="border-top-0">{{ totalHammerPrice | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideHammerVat">
      <td><b>{{ $t('Hammer VAT') }}</b></td>
      <td>{{ totalHammerVat | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideHammerAdjustment">
      <td><b>{{ $t('Hammer Adjustment') }}</b></td>
      <td>{{ totalHammerAdjustment | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideHammerAdjustmentVat">
      <td><b>{{ $t('Hammer Adjustment VAT') }}</b></td>
      <td>{{ totalHammerAdjustmentVat | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideCommission">
      <td><b>{{ $t('Commission') }}</b></td>
      <td>{{ totalCommission * -1 | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideCommissionVat">
      <td><b>{{ $t('Commission VAT') }}</b></td>
      <td>{{ totalCommissionVat * -1 | asCurrency(currency) }}</td>
    </tr>

    <template v-if="model.enable_shipping && !hideShipping">
      <tr>
        <td><b>{{ $t('Shipping') }}</b></td>
        <td>{{ shippingAmount * -1 | asCurrency(currency) }}</td>
      </tr>
      <tr v-if="!hideShippingVat">
        <td><b>{{ dynamicTexts.shippingVat }}</b></td>
        <td>{{ shippingVat * -1 | asCurrency(currency) }}</td>
      </tr>
    </template>

    <tr v-if="!hideLotHandlingCharge">
      <td><b>{{ $t('Entry Fee') }}</b></td>
      <td>{{ totalLotHandlingCharge * -1 | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideLotHandlingVat">
      <td><b>{{ $t('Entry Fee VAT') }}</b></td>
      <td>{{ totalLotHandlingVat * -1 | asCurrency(currency) }}</td>
    </tr>
    <tr v-if="!hideInsuranceFee">
      <td><b>{{ $t('L/D Warranty Fee') }}</b></td>
      <td>{{ totalInsuranceFee * -1 | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideInsuranceVat">
      <td><b>{{ $t('L/D Warranty VAT') }}</b></td>
      <td>{{ totalInsuranceVat * -1 | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideIntroductoryCommission">
      <td><b>{{ $t('Introductory Commission') }}</b></td>
      <td>{{ totalIntroductoryCommission | asCurrency(currency) }}</td>
    </tr>

    <tr v-if="!hideIntroductoryCommissionVat">
      <td><b>{{ $t('Introductory Commission VAT') }}</b></td>
      <td>{{ totalIntroductoryCommissionVat | asCurrency(currency) }}</td>
    </tr>

    <template v-if="!hideCharge">
      <template v-for="(charge,index) in extraCharges" >
        <tr :key="`total_charge${index}`">
          <td><b>{{ charge ? charge.name : '' }}</b></td>
          <td v-if="charge">{{ charge.totalAmount * -1 | asCurrency(currency) }}</td>
          <td v-else></td>
        </tr>
        <tr v-if="charge" :key="`total_charge_vat${index}`">
          <td><b>{{ charge ? `${dynamicTexts.vatOn} ${charge.name}` : '' }}</b></td>
          <td v-if="charge">{{ charge.totalVat * -1 | asCurrency(currency) }}</td>
          <td v-else></td>
        </tr>
      </template>
    </template>

    <tr class="total-amount">
      <td><b>{{ $t('Total') }}</b></td>
      <td>{{ totalAmount | asCurrency(currency) }}</td>
    </tr>
  </table>
</template>

<script>
  import BaseModel from "@/core/components/input-widget/BaseModel";
  import _ from 'lodash'
  import {createNamespacedHelpers} from "vuex";

  const {mapState: globalMapState, mapGetters: globalMapGetters} = createNamespacedHelpers('global');
  export default {
    name: "SettlementTotalsTable",
    props: {
      model: BaseModel,
      charges: Array,
      financialItems: Array,
      currency: {
        type: String,
        default: null
      },
      hideCommission: {type: Boolean, default: false},
      hideHammer: {type: Boolean, default: false},
      hideHammerVat: {type: Boolean, default: false},
      hideHammerAdjustment: {type: Boolean, default: false},
      hideHammerAdjustmentVat: {type: Boolean, default: false},
      hideCommissionVat: {type: Boolean, default: false},
      hideLotHandlingCharge: {type: Boolean, default: false},
      hideLotHandlingVat: {type: Boolean, default: false},
      hideInsuranceFee: {type: Boolean, default: false},
      hideInsuranceVat: {type: Boolean, default: false},
      hideShipping: {type: Boolean, default: false},
      hideShippingVat: {type: Boolean, default: false},
      hideCharge: {type: Boolean, default: false},
      hideIntroductoryCommission: {type: Boolean, default: true},
      hideIntroductoryCommissionVat: {type: Boolean, default: true},
      settlementType: {type: String, default: null},
      customLabels: {
        type: Object,
        required: false,
        default: () => {
          return {}
        }
      },
    },
    computed: {
      ...globalMapGetters(['isAvalaraEnabled']),
      ...globalMapState({
        dynamicTexts: state => state.dynamicTexts,
      }),
      totalHammerPrice() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.vendor_financial_price || 0), 0);
      },
      totalHammerVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.vendor_financial_price_vat || 0), 0);
      },
      totalHammerAdjustment() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.hammer_adjustment || 0), 0);
      },
      totalHammerAdjustmentVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.hammer_adjustment_vat || 0), 0);
      },
      totalCommission() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.commission || 0), 0);
      },
      totalCommissionVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.commission_vat || 0), 0);
      },
      totalLotHandlingCharge() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.lot_handling_charge || 0), 0);
      },
      totalLotHandlingVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.lot_handling_vat || 0), 0);
      },
      totalInsuranceFee() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.insurance_fee || 0), 0);
      },
      totalInsuranceVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.insurance_vat || 0), 0);
      },
      shippingAmount() {
        return parseFloat(this.model.shipping_amount || 0);
      },
      shippingVat() {
        return parseFloat(this.model.shipping_vat || 0);
      },
      totalIntroductoryCommission() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.introductory_commission || 0), 0);
      },
      totalIntroductoryCommissionVat() {
        return this.financialItems.reduce((a, b) => a + parseFloat(b.introductory_commission_vat || 0), 0);
      },
      extraCharges() {
        let charges = []
        let mappedData = _.groupBy(this.charges, 'description')
        Object.keys(mappedData).map(description => {
          charges.push({
            name: description,
            totalAmount: mappedData[description].reduce((a, b) => a + parseFloat(b.amount || 0), 0),
            totalVat: mappedData[description].reduce((a, b) => a + parseFloat(b.vat || 0), 0)
          })
        });
        return charges
      },
      totalCharge() {
        return this.charges.reduce((a, b) => a + parseFloat(b.amount || 0), 0)
      },
      totalChargeVat() {
        return this.charges.reduce((a, b) => a + parseFloat(b.vat || 0), 0)
      },
      fullHammerPrice() {
        return this.totalHammerPrice + this.totalHammerAdjustment
      },
      fullHammerVat() {
        return this.totalHammerVat + this.totalHammerAdjustmentVat - this.totalCommissionVat
      },
      totalAmount() {
        if (['auction_introductory', 'manual_introductory'].includes(this.settlementType)) {
          return this.totalIntroductoryCommission + this.totalIntroductoryCommissionVat
        }
        return this.fullHammerPrice + this.fullHammerVat -
            this.totalCharge - this.totalChargeVat -
            this.totalLotHandlingCharge - this.totalLotHandlingVat - this.totalCommission -
            this.totalInsuranceFee - this.totalInsuranceVat -
            this.shippingAmount - this.shippingVat
      },
    },
    methods: {
      getLabel(key) {
        let obj = {
          miscItem: this.$i18n.t('Misc. Charges'),
          miscItemVat: this.dynamicTexts.vatOnMiscCharges
        }
        return this.customLabels[key] ? this.customLabels[key] : obj[key]
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .totals-table {
    tr {
      td {
        text-align: right;
      }

      td:nth-child(2) {
        width: 30%;
      }
    }

    .total-amount {
      td {
        border-top: 1px solid $primary;
      }
    }
  }
</style>
